import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getSystemSettings } from '../services/systemSettingsService';
import { currencies } from '../utils/constants';

interface CurrencyInfo {
  code: string;
  symbol: string;
  label: string;
}

interface CurrencyContextType {
  currency: CurrencyInfo;
  loading: boolean;
  formatCurrency: (amount: number | undefined | null) => string;
  formatCurrencyWithCommas: (amount: number | undefined | null) => string;
  formatAmount: (amount: number | undefined | null) => string;
  formatAmountWithCommas: (amount: number | undefined | null) => string;
  getCurrencySymbol: () => string;
  getCurrencyCode: () => string;
  getCurrencyLabel: () => string;
  refreshCurrency: () => Promise<void>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrency] = useState<CurrencyInfo>({
    code: 'EGP',
    symbol: 'ج.م',
    label: 'جنيه مصري'
  });
  const [loading, setLoading] = useState(true);

  const loadCurrency = async () => {
    try {
      setLoading(true);
      const settings = await getSystemSettings();
      const currencyCode = settings.currency || 'EGP';
      
      // البحث عن معلومات العملة
      const currencyInfo = currencies.find(c => c.value === currencyCode);
      
      if (currencyInfo) {
        setCurrency({
          code: currencyInfo.value,
          symbol: currencyInfo.symbol,
          label: currencyInfo.label
        });
      }
    } catch (error) {
      console.error('Error loading currency settings:', error);
      // استخدام العملة الافتراضية في حالة الخطأ
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrency();
  }, []);

  /**
   * تنسيق المبلغ بالعملة الحالية
   * ملاحظة: هذه الدالة تعرض المبلغ بالعملة المختارة في الإعدادات
   * المبيعات الجديدة تُحفظ مع معلومات العملة للدقة
   */
  const formatCurrency = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) {
      return `0.00 ${currency.symbol}`;
    }
    return `${amount.toFixed(2)} ${currency.symbol}`;
  };

  /**
   * تنسيق المبلغ بالعملة الحالية مع فواصل الآلاف
   */
  const formatCurrencyWithCommas = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) {
      return `0.00 ${currency.symbol}`;
    }
    return `${amount.toLocaleString('ar-EG', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })} ${currency.symbol}`;
  };

  /**
   * تنسيق المبلغ بدون رمز العملة
   */
  const formatAmount = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) {
      return '0.00';
    }
    return amount.toFixed(2);
  };

  /**
   * تنسيق المبلغ بدون رمز العملة مع فواصل الآلاف
   */
  const formatAmountWithCommas = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) {
      return '0.00';
    }
    return amount.toLocaleString('ar-EG', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  /**
   * الحصول على رمز العملة فقط
   */
  const getCurrencySymbol = (): string => {
    return currency.symbol;
  };

  /**
   * الحصول على كود العملة فقط
   */
  const getCurrencyCode = (): string => {
    return currency.code;
  };

  /**
   * الحصول على اسم العملة
   */
  const getCurrencyLabel = (): string => {
    return currency.label;
  };

  /**
   * تحديث العملة من الإعدادات
   */
  const refreshCurrency = async (): Promise<void> => {
    await loadCurrency();
  };

  const value: CurrencyContextType = {
    currency,
    loading,
    formatCurrency,
    formatCurrencyWithCommas,
    formatAmount,
    formatAmountWithCommas,
    getCurrencySymbol,
    getCurrencyCode,
    getCurrencyLabel,
    refreshCurrency
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

/**
 * Hook لاستخدام العملة من Context
 */
export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
